import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import './RankedMatchmaking.css';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rank tier data (matches backend)
const RANK_TIERS = [
  { name: 'Pawn', tier: 1, min_elo: 0, max_elo: 999, icon: '♟️', color: '#8B4513' },
  { name: 'Knight', tier: 2, min_elo: 1000, max_elo: 1199, icon: '♞', color: '#708090' },
  { name: 'Bishop', tier: 3, min_elo: 1200, max_elo: 1399, icon: '♝', color: '#4169E1' },
  { name: 'Rook', tier: 4, min_elo: 1400, max_elo: 1599, icon: '♜', color: '#9932CC' },
  { name: 'Queen', tier: 5, min_elo: 1600, max_elo: 1899, icon: '♛', color: '#FFD700' },
  { name: 'King', tier: 6, min_elo: 1900, max_elo: 9999, icon: '♚', color: '#FF4500' },
];

const RankedMatchmaking = ({ 
  user, 
  token, 
  onClose, 
  onJoinGame,
  addNotification 
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('queue'); // queue, stats, leaderboard
  const [pool, setPool] = useState('classic'); // classic, pro_1, pro_2
  const [rankedStats, setRankedStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [seasonInfo, setSeasonInfo] = useState(null);
  const [inQueue, setInQueue] = useState(false);
  const [queueTime, setQueueTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user's ranked stats for current pool
  const fetchRankedStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/ranked/stats?pool=${pool}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      , withCredentials: true });
      const data = await response.json();
      if (data.success) {
        setRankedStats(data);
      }
    } catch (err) {
      logger.error('Failed to fetch ranked stats:', err);
    }
  }, [token, pool]);

  // Fetch leaderboard for current pool
  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ranked/leaderboard?pool=${pool}&limit=50`);
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) {
      logger.error('Failed to fetch leaderboard:', err);
    }
  }, [pool]);

  // Fetch season info
  const fetchSeasonInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ranked/seasons`);
      const data = await response.json();
      if (data.success) {
        setSeasonInfo(data);
      }
    } catch (err) {
      logger.error('Failed to fetch season info:', err);
    }
  }, []);

  useEffect(() => {
    fetchRankedStats();
    fetchLeaderboard();
    fetchSeasonInfo();
  }, [fetchRankedStats, fetchLeaderboard, fetchSeasonInfo]);

  // Queue timer
  useEffect(() => {
    let interval;
    if (inQueue) {
      interval = setInterval(() => {
        setQueueTime(prev => prev + 1);
      }, 1000);
    } else {
      setQueueTime(0);
    }
    return () => clearInterval(interval);
  }, [inQueue]);

  // Join ranked queue
  const handleJoinQueue = async () => {
    if (!token) {
      setError(t('ranked.pleaseLoginToPlay'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ranked/queue/join?pool=${pool}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        if (data.match_found) {
          addNotification?.('success', 'Match found!');
          onJoinGame?.(data.game_id);
        } else {
          setInQueue(true);
          addNotification?.('info', 'Joined ranked queue. Searching...');
        }
      } else {
        setError(data.detail || t('ranked.failedToJoinQueue'));
      }
    } catch (err) {
      setError(t('ranked.failedToJoinQueue'));
    } finally {
      setLoading(false);
    }
  };

  // Leave ranked queue
  const handleLeaveQueue = async () => {
    try {
      await fetch(`${API_URL}/api/ranked/queue/leave?pool=${pool}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInQueue(false);
      addNotification?.('info', 'Left ranked queue');
    } catch (err) {
      logger.error('Failed to leave queue:', err);
    }
  };

  // Poll for match while in queue
  useEffect(() => {
    let pollInterval;
    if (inQueue && token) {
      pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`${API_URL}/api/ranked/queue/status?pool=${pool}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          , withCredentials: true });
          const data = await response.json();
          
          if (!data.in_queue) {
            // Check if we were matched
            setInQueue(false);
          }
        } catch (err) {
          logger.error('Queue poll error:', err);
        }
      }, 3000);
    }
    return () => clearInterval(pollInterval);
  }, [inQueue, token, pool]);

  const formatQueueTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTierInfo = (elo) => {
    return RANK_TIERS.find(t => elo >= t.min_elo && elo <= t.max_elo) || RANK_TIERS[0];
  };

  const renderQueueTab = () => {
    const tier = rankedStats ? getTierInfo(rankedStats.elo) : RANK_TIERS[1];
    const poolLabel = pool === 'classic' ? 'Classic' : pool === 'pro_1' ? 'Pro 1' : 'Pro 2';
    
    return (
      <div className="ranked-queue-section">
        {/* Pool Selector */}
        <div className="setting-section" data-testid="ranked-pool-selector">
          <h3 style={{ marginBottom: '8px', fontSize: '0.95rem' }}>Ranked Pool</h3>
          <div className="embodiment-icons" data-testid="ranked-pool-buttons">
            {[
              { id: 'classic', icon: '⚔️', label: 'Classic' },
              { id: 'pro_1', icon: '👑', label: 'Pro 1' },
              { id: 'pro_2', icon: '🛡️', label: 'Pro 2' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`embodiment-icon-button ${pool === opt.id ? 'active' : ''}`}
                onClick={() => { if (!inQueue) setPool(opt.id); }}
                disabled={inQueue}
                data-testid={`ranked-pool-${opt.id}`}
              >
                <span className="icon">{opt.icon}</span>
                <span className="label">{opt.label}</span>
              </button>
            ))}
          </div>
          <p className="embodiment-note" style={{ marginTop: '6px', fontSize: '0.8rem' }}>
            Each pool has its own ELO ladder. {pool === 'pro_1' && 'Pro 1: any baseline move reclaims throne.'}{pool === 'pro_2' && 'Pro 2: only non-capture baseline moves reclaim throne; Duke can\'t touch opponent baseline.'}
          </p>
        </div>

        {/* Current Rank Display */}
        <div className="current-rank-display" style={{ borderColor: tier.color }}>
          <div className="rank-icon" style={{ color: tier.color }}>{tier.icon}</div>
          <div className="rank-info">
            <span className="rank-name" style={{ color: tier.color }}>{tier.name}</span>
            <span className="rank-elo">{rankedStats?.elo || 1200} ELO <small style={{opacity:0.7}}>({poolLabel})</small></span>
          </div>
          {rankedStats && rankedStats.progress_to_next < 100 && (
            <div className="rank-progress">
              <div 
                className="progress-bar" 
                style={{ 
                  width: `${rankedStats.progress_to_next}%`,
                  backgroundColor: tier.color 
                }}
              />
              <span className="progress-text">{Math.round(rankedStats.progress_to_next)}% to next rank</span>
            </div>
          )}
        </div>

        {/* Queue Button */}
        <div className="queue-controls">
          {inQueue ? (
            <>
              <div className="queue-searching">
                <div className="searching-spinner" />
                <span>Searching for opponent...</span>
                <span className="queue-timer">{formatQueueTime(queueTime)}</span>
              </div>
              <button 
                className="btn-cancel-queue"
                onClick={handleLeaveQueue}
                data-testid="cancel-queue-btn"
              >
                Cancel
              </button>
            </>
          ) : (
            <button 
              className="btn-find-match"
              onClick={handleJoinQueue}
              disabled={loading || !token}
              data-testid="find-match-btn"
            >
              {loading ? 'Joining...' : '⚔️ Find Ranked Match'}
            </button>
          )}
        </div>

        {/* Season Info */}
        {seasonInfo && (
          <div className="season-info">
            <span className="season-label">Season: {seasonInfo.current_season}</span>
            <span className="season-days">{seasonInfo.days_remaining} days remaining</span>
          </div>
        )}

        {/* Quick Stats */}
        {rankedStats && (
          <div className="quick-stats">
            <div className="stat-item">
              <span className="stat-value">{rankedStats.games_played}</span>
              <span className="stat-label">Games</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{rankedStats.wins}</span>
              <span className="stat-label">Wins</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">
                {rankedStats.games_played > 0 
                  ? Math.round((rankedStats.wins / rankedStats.games_played) * 100) 
                  : 0}%
              </span>
              <span className="stat-label">Win Rate</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{rankedStats.peak_elo}</span>
              <span className="stat-label">Peak ELO</span>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const renderStatsTab = () => {
    if (!rankedStats) {
      return <div className="no-stats">Play ranked games to see your stats!</div>;
    }

    const tier = getTierInfo(rankedStats.elo);

    return (
      <div className="ranked-stats-section">
        <div className="stats-header">
          <div className="large-rank-display" style={{ borderColor: tier.color }}>
            <span className="large-rank-icon" style={{ color: tier.color }}>{tier.icon}</span>
            <span className="large-rank-name" style={{ color: tier.color }}>{tier.name}</span>
            <span className="large-elo">{rankedStats.elo} ELO</span>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-title">Games Played</span>
            <span className="stat-number">{rankedStats.games_played}</span>
          </div>
          <div className="stat-card wins">
            <span className="stat-title">Wins</span>
            <span className="stat-number">{rankedStats.wins}</span>
          </div>
          <div className="stat-card losses">
            <span className="stat-title">Losses</span>
            <span className="stat-number">{rankedStats.losses}</span>
          </div>
          <div className="stat-card">
            <span className="stat-title">Win Rate</span>
            <span className="stat-number">
              {rankedStats.games_played > 0 
                ? Math.round((rankedStats.wins / rankedStats.games_played) * 100) 
                : 0}%
            </span>
          </div>
          <div className="stat-card streak">
            <span className="stat-title">Current Streak</span>
            <span className="stat-number">{rankedStats.win_streak} 🔥</span>
          </div>
          <div className="stat-card">
            <span className="stat-title">Best Streak</span>
            <span className="stat-number">{rankedStats.best_streak}</span>
          </div>
          <div className="stat-card peak">
            <span className="stat-title">Peak ELO</span>
            <span className="stat-number">{rankedStats.peak_elo}</span>
          </div>
          <div className="stat-card">
            <span className="stat-title">Season Games</span>
            <span className="stat-number">{rankedStats.season_games || 0}</span>
          </div>
        </div>

        {/* Rank Tiers Visual */}
        <div className="rank-tiers-display">
          <h4>Rank Tiers</h4>
          <div className="tiers-list">
            {RANK_TIERS.map((t) => (
              <div 
                key={t.name}
                className={`tier-item ${rankedStats.elo >= t.min_elo && rankedStats.elo <= t.max_elo ? 'current' : ''}`}
                style={{ borderColor: t.color }}
              >
                <span className="tier-icon" style={{ color: t.color }}>{t.icon}</span>
                <span className="tier-name">{t.name}</span>
                <span className="tier-range">{t.min_elo} - {t.max_elo === 9999 ? '∞' : t.max_elo}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderLeaderboardTab = () => {
    return (
      <div className="ranked-leaderboard-section">
        <h3>🏆 Ranked Leaderboard</h3>
        {seasonInfo && (
          <div className="leaderboard-season">
            Season: {seasonInfo.current_season}
          </div>
        )}
        
        <div className="leaderboard-table">
          <div className="leaderboard-header">
            <span className="col-rank">#</span>
            <span className="col-player">Player</span>
            <span className="col-tier">Tier</span>
            <span className="col-elo">ELO</span>
            <span className="col-winrate">Win Rate</span>
          </div>
          
          {leaderboard.length === 0 ? (
            <div className="no-leaderboard">No ranked players yet!</div>
          ) : (
            leaderboard.map((entry, index) => (
              <div 
                key={entry.user_id} 
                className={`leaderboard-row ${entry.user_id === user?.user_id ? 'current-user' : ''}`}
              >
                <span className="col-rank">
                  {index === 0 && '🥇'}
                  {index === 1 && '🥈'}
                  {index === 2 && '🥉'}
                  {index > 2 && entry.rank}
                </span>
                <span className="col-player">
                  <span className="player-avatar">
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt="" />
                    ) : '👤'}
                  </span>
                  {entry.name}
                </span>
                <span className="col-tier" style={{ color: entry.tier_color }}>
                  {entry.tier_icon} {entry.tier}
                </span>
                <span className="col-elo">{entry.elo}</span>
                <span className="col-winrate">{entry.win_rate}%</span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ranked-matchmaking-modal">
      <div className="ranked-matchmaking-content">
        <div className="ranked-header">
          <h2>⚔️ Ranked Matchmaking</h2>
          <button className="close-btn" onClick={onClose} data-testid="close-ranked-modal">×</button>
        </div>

        <div className="ranked-tabs">
          <button 
            className={`tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            🎮 Play
          </button>
          <button 
            className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 Stats
          </button>
          <button 
            className={`tab-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            🏆 Leaderboard
          </button>
        </div>

        <div className="ranked-body">
          {activeTab === 'queue' && renderQueueTab()}
          {activeTab === 'stats' && renderStatsTab()}
          {activeTab === 'leaderboard' && renderLeaderboardTab()}
        </div>
      </div>
    </div>
  );
};

export default RankedMatchmaking;
